import { nanoid } from 'nanoid';
import { v4 as uuid } from 'uuid';
import {
  IPlant,
  IUnit,
  IPlantUnitEntitiesColored,
  IAppConfig,
  IPanel,
  ITag,
  ICameraPosition,
  ITagAggregateId,
  IWorkPackage,
  IVolume,
  IBITag,
  IReport,
  IViewerSettings,
  IComment,
  IMultiplePKConfig,
  IUser,
  IProject
} from '../data-models/dataModels';
import { ICustomStatus } from '../utils/indexedDb/customStatus';
import {
  SELECT_MODELS,
  SET_MODEL_OFFSET,
  SET_VIEWER_INSTANCE,
  ViewerActionType,
  COLOR_OBJETS_MODEL,
  COLOR_BI_TAGS,
  ConfigActionType,
  INIT_CONFIG,
  SELECT_TAGS,
  SELECT_TAG_NAMES,
  ISOLATE_TAGS,
  ISOLATE_TAG_NAMES,
  OPEN_PANEL,
  SELECTED_OBJECT_IN_VIEWER,
  CAMERA_POSITION_CHANGED,
  EXTENSION_ACTIVE_CHANGED,
  CHANGE_POSITION_CAMERA,
  DRAG,
  DROP,
  FINISH_DND,
  DndActionType,
  SELECT_AGGREGATE_TAGS,
  SELECTED_STATUS_MODES,
  SET_CUSTOM_ENTITES_COLORED,
  INIT_PERMISSIONS,
  PermissionActionType,
  WPActionType,
  SELECT_ISOLATE_WP,
  SET_PREV_WP,
  SET_COLORED_WP_IDS,
  CUTPLANES_CHANGED,
  SELECT_CUTPLANES,
  EDIT_CUTPLANES,
  OPEN_BI_REPORT,
  SET_SCREENSHOT_ACTION,
  SET_SCREENSHOT_URL,
  CHANGE_SELECTION_LEVEL,
  SET_MAX_SELECTION_LEVEL,
  CREATE_SNAPSHOT,
  SET_MODEL_PROPERTIES,
  SAVE_VIEWER_SETTINGS,
  APPLY_VIEWER_SETTINGS,
  SET_IS_MOBILE,
  SET_IS_LESSTHANXSMALL,
  CommentActionType,
  SELECT_ISOLATE_Comment,
  SET_PREV_Comment,
  SET_STEP_INDEX,
  SET_RUN,
  SET_IS_MANUAL_INCREMENT,
  SET_IDENTIFIER,
  TourActionType,
  SET_PANEL_SCROLL_POSITION,
  CLOSE_PANEL,
  OPEN_PANELS,
  UPDATE_OPEN_PANELS,
  UPDATE_PANEL_POSITION,
  PushpinOpenType,
  OPEN_PUSHPIN_DETAIL,
  IS_PUSHPIN_COMMENT_ACTIVE,
  SELECTED_COMMENTS_FOR_PUSHPIN,
  SET_IS_REFRESHING,
  SET_LOADING_ITEMS,
  UPDATE_THUMBNAIL_IMAGE,
  SET_MARKUP_ACTIVE,
  REFRESH_PUSHPIN_DATA,
  SET_PUSHPIN_DEEP_LINK,
  SET_LOADED_URN_LIST,
  SET_IS_DIFF_TOOL_LOADED,
  SET_PBI_REPORT_STATE,
  SET_IS_PBI_MODE_ACTIVE,
  SET_IS_PBI_NEW_WINDOW_ACTIVE,
  TRIGGER_PBI_REPORT_REFRESH,
  SET_IS_REFRESH_DOCUMENTS_IN_COMMENT_PANEL,
  SET_CUTPLANE_FILTER_MODE,
  SET_HOME_USER,
  UserActionTypes,
  SET_PROJECT_USER,
  SET_PROJECT_LIST,
  UPDATE_CUSTOM_STATUS_GRID,
  UPDATE_CUSTOM_STATUS_PROPERTY_ENABLE,
  UPDATE_CUSTOM_STATUS_EDIT_MODE,
  UPDATE_CUSTOM_STATUS_EDIT_GRID,
  UPDATE_CUSTOM_STATUS_PUBLISHED_IN_EDIT_MODE,
  RESET_CUSTOM_STATUS_STATE
} from './actionTypes';
import { WorkpackageActionEnum, ScreenShotActionEnum ,CommentActionEnum, IPushpinComments} from './stateTypes';

export const selectModel = (
  plant: IPlant,
  units: IUnit[],
  selectedTagInViewer?: ITag[]
): ViewerActionType => {

  const filteredTags = units.length > 0 
    ? selectedTagInViewer?.filter((tag: ITag) =>
        tag.UnitName !== undefined && units.some(unit => unit.unitName === tag.UnitName)
      ) || []
    : [];

  return {
    type: SELECT_MODELS,
    payload: {
      selectedPlant: plant,
      selectedUnits: units,
      selectedTagInViewer: filteredTags,
    }
  };
};

export const saveViewerSettings = (
  viewerSettings: IViewerSettings | null
): ViewerActionType => ({
  type: SAVE_VIEWER_SETTINGS,
  payload: {
    viewerSettings: viewerSettings
  }
});

export const applyViewerSettings = (
  viewerSettings: IViewerSettings | null
): ViewerActionType => ({
  type: APPLY_VIEWER_SETTINGS,
  payload: {
    applyViewerSettings: viewerSettings
  }
});

export const setModelOffset = (
  offset: THREE.Vector3 | null
): ViewerActionType => ({
  type: SET_MODEL_OFFSET,
  payload: {
    globalOffset: offset
  }
});

export const setViewerInstance = (
  viewerInstance: IViewerComponent | null
): ViewerActionType => ({
  type: SET_VIEWER_INSTANCE,
  payload: {
    viewerInstance: viewerInstance
  }
});

export const setModelProperties = (
  properties: string[] | null
): ViewerActionType => ({
  type: SET_MODEL_PROPERTIES,
  payload: {
    properties
  }
});

export const colorObjects = (
  entitiesColored: IPlantUnitEntitiesColored[] | null
): ViewerActionType => ({
  type: COLOR_OBJETS_MODEL,
  payload: {
    entitiesColored
  }
});

export const setColoredWPIds = (
  coloredWPIds: number[]
): WPActionType => ({
  type: SET_COLORED_WP_IDS,
  payload: {
    coloredWPIds
  }
});

export const colorBITags = (
  coloredBITags: IBITag[] | null,
  multiplePKConfig?: IMultiplePKConfig,
  searchType?: string
): ViewerActionType => ({
  type: COLOR_BI_TAGS,
  payload: {
    coloredBITags,
    multiplePKConfig,
    searchType
  }
});

export const initConfig = (config: IAppConfig): ConfigActionType => ({
  type: INIT_CONFIG,
  payload: config
});
export const selectTags = (selectedTags: ITag[] | null): ViewerActionType => ({
  type: SELECT_TAGS,
  payload: {
    selectedTags
  }
});

export const selectTagNames = (
  selectedTagNames: string[] | null,
  selectedCheckbox?: boolean,
  multiplePKConfig?: IMultiplePKConfig,
  searchType?: string
): ViewerActionType => ({
  type: SELECT_TAG_NAMES,
  payload: {
    selectedTagNames,
    selectedCheckbox,
    multiplePKConfig,
    searchType
  }
});

export const selectAggregateTags = (
  selectedAggregateTags: ITagAggregateId[]
): ViewerActionType => ({
  type: SELECT_AGGREGATE_TAGS,
  payload: {
    selectedAggregateTags
  }
});
export const isolateTags = (isolatedTags: ITag[] | null): ViewerActionType => ({
  type: ISOLATE_TAGS,
  payload: {
    isolatedTags
  }
});

export const isolateTagNames = (
  isolatedTagNames: string[] | null,
  multiplePKConfig?: IMultiplePKConfig,
  searchType?: string
): ViewerActionType => ({
  type: ISOLATE_TAG_NAMES,
  payload: {
    isolatedTagNames,
    multiplePKConfig,
    searchType
  }
});

export const selectTagInViewer = (
  selectedTagInViewer: ITag[]
): ViewerActionType => ({
  type: SELECTED_OBJECT_IN_VIEWER,
  payload: {
    selectedTagInViewer
  }
});
export const openPanel = (openPanel: IPanel): ViewerActionType => ({
  type: OPEN_PANEL,
  payload: openPanel
});

export const updateOpenPanels = (panels: IPanel[]) => ({
  type: UPDATE_OPEN_PANELS,
  payload: panels,
});

export const openPanels = (openPanels: IPanel[]): ViewerActionType => ({
  type: OPEN_PANELS,
  payload: openPanels
});

export const closePanel = (panelId: string): ViewerActionType => ({
  type: CLOSE_PANEL,
  payload: { panelId, data: {} }
});

export const cameraPositionChanged = (
  cameraPosition: ICameraPosition
): ViewerActionType => ({
  type: CAMERA_POSITION_CHANGED,
  payload: {
    cameraPosition
  }
});
export const markupGuiActivated = (
  extension: boolean
): ViewerActionType => ({
  type: EXTENSION_ACTIVE_CHANGED,
  payload: {
    extension
  }
});
export const changePositionCamera = (
  changePositionCamera: ICameraPosition
): ViewerActionType => ({
  type: CHANGE_POSITION_CAMERA,
  payload: {
    changePositionCamera
  }
});

export const createSnapshot = (
  createdSnapshotId: number
): ViewerActionType => ({
  type: CREATE_SNAPSHOT,
  payload: {
    createdSnapshotId
  }
});

export const setScreenShotAction = (
  screenshotAction: ScreenShotActionEnum
): ViewerActionType => ({
  type: SET_SCREENSHOT_ACTION,
  payload: {
    screenshotAction
  }
});

export const setScreenShotUrl = (screenshotUrl: string): ViewerActionType => ({
  type: SET_SCREENSHOT_URL,
  payload: {
    screenshotUrl
  }
});

export const selectedStatusModes = (
  selectedStatusModes: string[]
): ViewerActionType => ({
  type: SELECTED_STATUS_MODES,
  payload: {
    selectedStatusModes
  }
});

export const setCustomEntitiesColored = (
  customEntitiesColored: IPlantUnitEntitiesColored[] | null
): ViewerActionType => ({
  type: SET_CUSTOM_ENTITES_COLORED,
  payload: {
    customEntitiesColored
  }
});

export const openBIReport = (
  openedBIReport: IReport | null
): ViewerActionType => ({
  type: OPEN_BI_REPORT,
  payload: {
    openedBIReport
  }
});

export const changeSelectionLevel = (
  selectionLevel: number
): ViewerActionType => ({
  type: CHANGE_SELECTION_LEVEL,
  payload: {
    selectionLevel
  }
});

export const setMaxSelectionLevel = (
  maxSelectionLevel: number
): ViewerActionType => ({
  type: SET_MAX_SELECTION_LEVEL,
  payload: {
    maxSelectionLevel
  }
});

export const drag = (dragType: string, item: any): DndActionType => ({
  type: DRAG,
  payload: {
    isDragging: true,
    dragType,
    item
  }
});
export const drop = (): DndActionType => ({
  type: DROP,
  payload: {
    isDragging: false
  }
});
export const finishDnd = (dropStatus: string): DndActionType => ({
  type: FINISH_DND,
  payload: {
    dropStatus
  }
});
export const initPermissions = (
  permissions: string[],
  username: string
): PermissionActionType => ({
  type: INIT_PERMISSIONS,
  payload: {
    permissions,
    username
  }
});

export const setHomeUser = (
  homeUser: IUser
): UserActionTypes => ({
  type: SET_HOME_USER,
  payload: homeUser
});

export const setProjectUser = (
  projectUser: IUser
): UserActionTypes => ({
  type: SET_PROJECT_USER,
  payload: projectUser
});

export const setProjectList = (
  projects: IProject[]
): UserActionTypes => ({
  type: SET_PROJECT_LIST,
  payload: projects
});

export const selectIsolateWP = (
  workpackage: IWorkPackage[] | [],
  workpackageAction?: WorkpackageActionEnum
): WPActionType => ({
  type: SELECT_ISOLATE_WP,
  payload: {
    workpackage: workpackage || [],
    workpackageAction
  }
});
export const setPrevWP = (
  workpackage: IWorkPackage[] | [],
  workpackageAction?: WorkpackageActionEnum
): WPActionType => ({
  type: SET_PREV_WP,
  payload: {
    workpackage: workpackage || [],
    workpackageAction
  }
});
export const cutplanesChanged = (
  cutplanes: THREE.Vector4[] | null
): ViewerActionType => ({
  type: CUTPLANES_CHANGED,
  payload: {
    cutplanes
  }
});
export const selectCutplanes = (
  selectedVolume: IVolume | null
): ViewerActionType => ({
  type: SELECT_CUTPLANES,
  payload: {
    selectedVolume
  }
});

export const editCutplanes = (
  editedVolume: IVolume | null
): ViewerActionType => ({
  type: EDIT_CUTPLANES,
  payload: {
    editedVolume
  }
});

export const setCutplaneFilterMode = (
  cutplaneFilterMode: 'strict' | 'partial'
): ViewerActionType => ({
  type: SET_CUTPLANE_FILTER_MODE,
  payload: {
    cutplaneFilterMode
  }
});

export const setIsMobile = (
  isMobile: boolean
): ViewerActionType => ({
  type: SET_IS_MOBILE,
  payload: {
    isMobile
  }
});

export const setIsLessThanXSmall = (
  isLessThanXSmall: boolean
): ViewerActionType => ({
  type: SET_IS_LESSTHANXSMALL,
  payload: {
    isLessThanXSmall
  }
});

export const setPanelScrollPosition = (
  containerId: string,
  scrollPosition: number
  ) => ({
  type: SET_PANEL_SCROLL_POSITION,
  payload: {
    containerId,
    scrollPosition,  
  }
});


export const selectIsolateComment = (
  comment: IComment | null,
  CommentAction?: CommentActionEnum
): CommentActionType => ({
  type: SELECT_ISOLATE_Comment,
  payload: {
    comment: comment || undefined,
    CommentAction
  }
});
export const setPrevComment = (
  comment: IComment | null,
  CommentAction?: CommentActionEnum
): CommentActionType => ({
  type: SET_PREV_Comment,
  payload: {
    comment: comment || undefined,
    CommentAction
  }
});

export const setStepIndex = (
  stepIndex: number
): TourActionType => ({
  type: SET_STEP_INDEX,
  payload: {
    stepIndex
  }
});

export const setRun = (
  run: boolean
): TourActionType => ({
  type: SET_RUN,
  payload: {
    run
  }
});

export const setIsManualIncrement = (
  isManualIncrement: boolean
): TourActionType => ({
  type: SET_IS_MANUAL_INCREMENT,
  payload: {
    isManualIncrement
  }
});

export const setIdentifier = (
  identifier: string | null
): TourActionType => ({
  type: SET_IDENTIFIER,
  payload: {
    identifier
  }
});

export const updatePanelPosition = (panelId: string, position: { left: string, top: string }): ViewerActionType => ({
  type: UPDATE_PANEL_POSITION,
  payload: {
    panelId,
    position,
  }
});
//forcefully refresh snapshot for Markup and BI report 

export const setIsRefreshing = (isRefreshing: boolean): ViewerActionType => ({
  type: SET_IS_REFRESHING,
  payload: isRefreshing,
});

export const setLoadingItems = (loadingItems: string[]): ViewerActionType => ({
  type: SET_LOADING_ITEMS,
  payload: loadingItems
});

export const updateThumbnailImage = (imageName: string, image: string,lastRefreshDate: string) => ({
  type: UPDATE_THUMBNAIL_IMAGE,
  payload: { imageName , image , lastRefreshDate } 
});

export const setMarkupActive = (isActive: boolean): ViewerActionType => ({
  type: SET_MARKUP_ACTIVE,
  payload: isActive,
});

/** Pushpin Actions */
export const openPushpinDetailPanel = (
  pushpinId: number | null,
  uniqueId?: string,
): PushpinOpenType => ({
  type: OPEN_PUSHPIN_DETAIL,
  payload: {
    uniqueId,
    pushpinId,
  }
});

export const isPushpinCommentActive = (
  {isPushpinCommentActive, pushpinId} : {isPushpinCommentActive: boolean, pushpinId: number | null}
): PushpinOpenType => ({
  type : IS_PUSHPIN_COMMENT_ACTIVE,
  payload: {
    isPushpinCommentActive,
    pushpinId : pushpinId,
  }
});

export const selectedCommentsForPushpin = (
  comments : IPushpinComments[]
): PushpinOpenType => ({
  type : SELECTED_COMMENTS_FOR_PUSHPIN,
  payload: {
    comments: comments
  }
});

export const refreshPushpinPanel = () => ({
  type : REFRESH_PUSHPIN_DATA,
  payload: {
    refresh: nanoid()
  }
})

export const setPushpinDeepLink = (
  deepLinkPushpinId: number | null,
  deepLinkOpenCallout = false,
): PushpinOpenType => ({
  type: SET_PUSHPIN_DEEP_LINK,
  payload: {
    deepLinkPushpinId,
    deepLinkOpenCallout,
  },
});
/** Endof pushpin Actions */

/** Diff Tool */
export const setLoadedUrnList = (
  urnList : string[]
): ViewerActionType => ({
  type : SET_LOADED_URN_LIST,
  payload: {
    loadedUrnList: urnList,
  }
});

export const setDiffToolStatus = (
  isLoaded : boolean
): ViewerActionType => ({
  type : SET_IS_DIFF_TOOL_LOADED,
  payload: {
    isDiffToolLoaded: isLoaded,
  }
});

export const setShowExtendedPropertyPanel = (show: boolean, data?: any) => ({
  type: 'SET_SHOW_EXTENDED_PROPERTY_PANEL',
  payload: { show, data }
});


/** PBI Report State */
export const setPBIReportState = (
  state : string,
  buttonPressed:string[]
): ViewerActionType => ({
  type : SET_PBI_REPORT_STATE,
  payload: {
    PbiState: state,
    PbiButtonPressed: buttonPressed
  }
});


/** SET_IS_PBI_MODE_ACTIVE */
export const setIsPBIModeActive = (
  isPBIModeActive : boolean,
): ViewerActionType => ({
  type : SET_IS_PBI_MODE_ACTIVE,
  payload: {
    isPBIModeActive: isPBIModeActive
  }
});

/** SET_IS_PBI_NEW_WINDOW_ACTIVE */
export const setIsPBINewWindowActive = (
  isPBINewWindowActive : boolean,
): ViewerActionType => ({
  type : SET_IS_PBI_NEW_WINDOW_ACTIVE,
  payload: {
    isPBINewWindowActive: isPBINewWindowActive
  }
});

export const triggerPBIReportRefresh = (): ViewerActionType => ({
  type: TRIGGER_PBI_REPORT_REFRESH,
  payload: {
    pbiReportRefreshActionId: uuid()
  }
});

/** This state is only created to update documents list in comment panel */
export const refreshDocumentsInCommentPanel = (
  uniqueId : string,
): ViewerActionType => ({
  type : SET_IS_REFRESH_DOCUMENTS_IN_COMMENT_PANEL,
  payload: {
    refreshDocumentsInCommentPanel: uniqueId
  }
});


export const updateCustomStatusGrid = (grid: ICustomStatus[]) => ({
  type: UPDATE_CUSTOM_STATUS_GRID,
  payload: grid
});

export const updateCustomStatusPropertyEnable = (isEnabled: boolean) => ({
  type: UPDATE_CUSTOM_STATUS_PROPERTY_ENABLE,
  payload: isEnabled
});

export const updateCustomStatusEditMode = (isEditMode: boolean) => ({
  type: UPDATE_CUSTOM_STATUS_EDIT_MODE,
  payload: isEditMode
});

export const updateCustomStatusEditGrid = (editGrid: ICustomStatus[]) => ({
  type: UPDATE_CUSTOM_STATUS_EDIT_GRID,
  payload: editGrid
});

export const updateCustomStatusPublishedInEditMode = (isPublished: boolean) => ({
  type: UPDATE_CUSTOM_STATUS_PUBLISHED_IN_EDIT_MODE,
  payload: isPublished
});

export const resetCustomStatusState = () => ({
  type: RESET_CUSTOM_STATUS_STATE
});